import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useState } from 'react'

import { unwrapApiData } from '@/api/unwrapApiResult'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

interface Props {
  resourceLabel: string
  terminateFn: (ids: string[]) => Promise<{
    response: Response
    data?: unknown
    error?: unknown
  }>
}

// Off-page selections fall back to their id.
export function labelsForIds(
  ids: string[],
  rows: readonly { id: string; name: string }[] = [],
) {
  const names = new Map(rows.map((r) => [r.id, r.name]))
  return ids.map((id) => names.get(id) ?? id)
}

export function useResourceTermination<T extends { id: string; name: string }>({
  resourceLabel,
  terminateFn,
}: Props) {
  const queryClient = useQueryClient()
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [terminateTarget, setTerminateTarget] = useState<T | null>(null)
  const [bulkTerminateOpen, setBulkTerminateOpen] = useState(false)

  const doTerminate = async (ids: string[]) => {
    const result = await terminateFn(ids)
    unwrapApiData(result, `Failed to terminate ${resourceLabel}`)
    void message.success(`${ids.length} ${resourceLabel}(s) terminated`)
    setSelectedRowKeys([])
    setTerminateTarget(null)
    setBulkTerminateOpen(false)
    invalidateAdminCaches(queryClient)
  }

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    terminateTarget,
    setTerminateTarget,
    doTerminate,
    bulkTerminateOpen,
    openBulkTerminate: () => setBulkTerminateOpen(true),
    closeBulkTerminate: () => setBulkTerminateOpen(false),
  }
}
